import { TeamlessCollaborationCreateTeamMembers } from './TeamlessCollaborationCreateTeamMembers';
import React, { useState } from 'react';
import { forNamespace } from 'app/common/lib/util/i18n';
import styles from './TeamlessCollaborationCreateTeam.less';
import {
  useTeamlessCollaborationCreateTeamAddMembersMutation,
  useTeamlessCollaborationCreateTeamSubmitMutation,
} from '@trello/graphql/generated';
import {
  Button,
  ButtonSizes,
  ButtonTypes,
} from 'app/common/components/controls/button/button';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { Spinner } from 'app/src/components/Spinner';

const format = forNamespace('teamless collaboration prompt');

const noop = () => {};

interface TeamlessCollaborationCreateTeamProps {
  boardId: string;
  onTeamCreated: (orgName: string) => void;
}

export const TeamlessCollaborationCreateTeam: React.FunctionComponent<TeamlessCollaborationCreateTeamProps> = ({
  boardId,
  onTeamCreated,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [isTeamNameValid, setIsTeamNameValid] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  const [
    createOrganization,
  ] = useTeamlessCollaborationCreateTeamSubmitMutation();

  const [
    addMembersToOrg,
  ] = useTeamlessCollaborationCreateTeamAddMembersMutation();

  const onChangeTeamName: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newTeamName = e.target.value;
    setTeamName(newTeamName);
    setIsTeamNameValid(newTeamName.trim().length > 0);
  };

  const onCreateTeamSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);

    let orgName;
    try {
      // 1. Create Team
      const { data } = await createOrganization({
        variables: {
          type: 'default',
          displayName: teamName.trim(),
        },
      });
      orgName = data && data.createOrganization && data.createOrganization.name;
      const orgId =
        data && data.createOrganization && data.createOrganization.id;

      // 2. Add the chosen members to the team
      if (orgId) {
        await addMembersToOrg({
          variables: {
            orgId,
            users: selectedMembers.map((idMember) => ({ id: idMember })),
          },
        });
      }
    } catch (err) {
      Alerts.showLiteralText(err.message, 'error', 'alert', 5000);
      setIsSubmitted(false);
    }

    if (orgName) {
      onTeamCreated(orgName);
    }
  };

  const isButtonEnabled = isTeamNameValid && !isSubmitted;

  return (
    <div className={styles.createTeam}>
      <label
        className={styles.createTeamInputLabel}
        htmlFor="teamlessCollaborationCreateTeamInput"
      >
        {format(['create team form', 'input label'])}
      </label>
      <input
        id="teamlessCollaborationCreateTeamInput"
        className={styles.createTeamInput}
        type="text"
        autoComplete="off"
        spellCheck={false}
        onChange={onChangeTeamName}
        autoFocus={true}
        placeholder={format(['create team form', 'input placeholder'])}
        value={teamName}
        maxLength={100}
        disabled={isSubmitted}
      />
      <TeamlessCollaborationCreateTeamMembers
        boardId={boardId}
        onSelectMember={(currentSelectedMembers) =>
          setSelectedMembers(currentSelectedMembers)
        }
      />
      <Button
        buttonType={
          isButtonEnabled ? ButtonTypes.PRIMARY : ButtonTypes.DISABLED
        }
        className={styles.createTeamButton}
        onClick={isButtonEnabled ? onCreateTeamSubmit : noop}
        size={ButtonSizes.FULLWIDTH}
      >
        {isSubmitted ? (
          <Spinner centered />
        ) : (
          format(['create team form', 'create team'])
        )}
      </Button>
    </div>
  );
};
